/**
 * PartnerOfficesPage — قائمة مكاتب الشركاء (Jun 2026)
 * Route: /control/cloudgm/customer-dropdowns/partner-offices
 * Dropdown used by "رسوم إضافية لمكتب آخر" inside per-emirate service pricing.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Edit3, Loader2, Plus, Power, Sparkles, Trash2 } from 'lucide-react';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerOfficesPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/partner-offices`, { headers: getAuthHeaders() });
      setItems(r.data?.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (o) => {
    try {
      await axios.put(`${API}/admin/partner-offices/${o.id}`, { ...o, is_active: !o.is_active }, { headers: getAuthHeaders() });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const remove = async (o) => {
    if (!window.confirm(t(`حذف المكتب "${o.name_ar}"؟`, `Delete "${o.name_en}"?`))) return;
    try {
      await axios.delete(`${API}/admin/partner-offices/${o.id}`, { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted')); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <DashboardHeader showBackButton backPath="/control/cloudgm/customer-dropdowns" />
      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
            { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
            { label_ar: 'القوائم المنسدلة للتحكم بمنصة العملاء', label_en: 'Customer Platform Dropdown Lists', path: '/control/cloudgm/customer-dropdowns' },
            { label_ar: 'قائمة مكاتب الشركاء', label_en: 'Partner Offices List', path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="partner-offices-page">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">{t('قائمة مكاتب الشركاء', 'Partner Offices List')}</h1>
            <p className="text-sm text-white/50 mt-1 max-w-2xl">
              {t('اسم المكتب بالعربي والإنجليزي (مع ترجمة الذكاء الاصطناعي). تظهر هذه القائمة عند اختيار «رسوم إضافية لمكتب آخر» في أسعار الإمارات داخل الخدمات.',
                 'Office name in Arabic & English (AI translate). This list appears when choosing "Extra fee for another office" inside per-emirate service pricing.')}
            </p>
          </div>
          <Button onClick={() => setEditing({})} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="office-add-btn">
            <Plus className="w-4 h-4 me-1" />{t('إضافة مكتب', 'Add office')}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16 text-white/40"><Loader2 className="w-7 h-7 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-white/10 text-white/40" data-testid="offices-empty">
            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {t('لا توجد مكاتب بعد — أضف أول مكتب شريك', 'No offices yet — add the first partner office')}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((o) => (
              <div key={o.id} className={`rounded-2xl border p-4 flex flex-wrap items-center gap-4 ${o.is_active ? 'border-white/10 bg-white/[0.02]' : 'border-white/5 bg-white/[0.01] opacity-60'}`} data-testid={`office-row-${o.id}`}>
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-emerald-400" /></div>
                <div className="min-w-[160px] flex-1">
                  <div className="text-white font-bold">{o.name_ar}</div>
                  <div className="text-white/50 text-xs" dir="ltr">{o.name_en}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => toggleActive(o)} className={`h-9 px-3 rounded-lg border text-xs flex items-center gap-1 ${o.is_active ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10' : 'border-white/10 text-white/50'}`} data-testid={`office-toggle-${o.id}`}>
                    <Power className="w-3.5 h-3.5" />{o.is_active ? t('مفعّل', 'Active') : t('متوقف', 'Off')}
                  </button>
                  <button onClick={() => setEditing(o)} className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 flex items-center justify-center" data-testid={`office-edit-${o.id}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => remove(o)} className="h-9 w-9 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 flex items-center justify-center" data-testid={`office-delete-${o.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editing !== null && <OfficeDialog o={editing} t={t} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function OfficeDialog({ o, t, onClose, onSaved }) {
  const isEdit = Boolean(o?.id);
  const [nameAr, setNameAr] = useState(o?.name_ar || '');
  const [nameEn, setNameEn] = useState(o?.name_en || '');
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const canSave = nameAr.trim() && nameEn.trim() && !saving;

  const translate = async () => {
    if (!nameAr.trim()) { toast.error(t('اكتب الاسم بالعربي أولاً', 'Enter the Arabic name first')); return; }
    setTranslating(true);
    try {
      const r = await axios.post(`${API}/translate`, { text: nameAr.trim(), source: 'ar', target: 'en' }, { headers: getAuthHeaders() });
      if (r.data?.translated) setNameEn(r.data.translated);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setTranslating(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = { name_ar: nameAr.trim(), name_en: nameEn.trim(), is_active: o?.is_active ?? true, sort_order: o?.sort_order ?? 0 };
      if (isEdit) await axios.put(`${API}/admin/partner-offices/${o.id}`, body, { headers: getAuthHeaders() });
      else await axios.post(`${API}/admin/partner-offices`, body, { headers: getAuthHeaders() });
      toast.success(t('تم الحفظ', 'Saved')); onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(' • ') : (d || e.message));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(x) => !x && onClose()}>
      <DialogContent className="bg-[#0b0b14] border-white/10 text-white sm:max-w-lg" data-testid="office-dialog">
        <DialogHeader><DialogTitle>{isEdit ? t('تعديل مكتب', 'Edit office') : t('إضافة مكتب شريك', 'Add partner office')}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-white/60">{t('اسم المكتب (عربي) *', 'Office name (Arabic) *')}</label>
            <Input value={nameAr} onChange={(e) => setNameAr(e.target.value)} placeholder={t('مثال: مكتب الطباعة — تسهيل', 'e.g. مكتب تسهيل')} className="bg-white/5 border-white/10 mt-1" data-testid="office-name-ar" />
          </div>
          <div>
            <label className="text-xs text-white/60">{t('اسم المكتب (إنجليزي) *', 'Office name (English) *')}</label>
            <div className="flex gap-2 mt-1">
              <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="e.g. Tasheel Typing Office" dir="ltr" className="bg-white/5 border-white/10" data-testid="office-name-en" />
              <Button type="button" variant="outline" onClick={translate} disabled={translating} className="border-[#d4af37]/40 text-[#d4af37] shrink-0" data-testid="office-translate-btn">
                {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white/70">{t('إلغاء', 'Cancel')}</Button>
          <Button onClick={save} disabled={!canSave} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="office-save-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin me-1" />}{isEdit ? t('حفظ التغييرات', 'Save changes') : t('إضافة', 'Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
